import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

interface PayComponent {
  id: number;
  component_code: string;
  component_name: string;
  component_type: 'addition' | 'deduction';
  recurring_flag: boolean;
  taxable_flag: boolean;
  pensionable_flag: boolean;
}

interface PayComponentForm {
  component_code: string;
  component_name: string;
  component_type: 'addition' | 'deduction';
  recurring_flag: boolean;
  taxable_flag: boolean;
  pensionable_flag: boolean;
}

const emptyForm: PayComponentForm = {
  component_code: '',
  component_name: '',
  component_type: 'addition',
  recurring_flag: true,
  taxable_flag: false,
  pensionable_flag: false,
};

export function PayComponents() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<PayComponent[] | null>(null);
  const [editing, setEditing] = useState<'new' | PayComponent | null>(null);
  const [form, setForm] = useState<PayComponentForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<PayComponent[]>('/pay-components').then((res) => setItems(res.data));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: PayComponent) => {
    setForm({
      component_code: item.component_code,
      component_name: item.component_name,
      component_type: item.component_type,
      recurring_flag: item.recurring_flag,
      taxable_flag: item.taxable_flag,
      pensionable_flag: item.pensionable_flag,
    });
    setErrors({});
    setEditing(item);
  };

  const set =
    (key: keyof PayComponentForm) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const toggle = (key: 'recurring_flag' | 'taxable_flag' | 'pensionable_flag') => (e: ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [key]: e.target.checked });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/pay-components', form);
      else if (editing) await api.put(`/pay-components/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: PayComponent) => {
    if (!(await confirmDelete(`Delete "${item.component_name}"?`))) return;
    try {
      await api.delete(`/pay-components/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Pay Components</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add
        </button>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>Recurring</th>
                <th>Taxable</th>
                <th>Pensionable</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td className="muted">{item.component_code}</td>
                  <td>{item.component_name}</td>
                  <td>
                    <span className={`badge ${item.component_type === 'addition' ? 'badge-active' : 'badge-cancelled'}`}>
                      {item.component_type}
                    </span>
                  </td>
                  <td className="muted">{item.recurring_flag ? 'Yes' : 'No'}</td>
                  <td className="muted">{item.taxable_flag ? 'Yes' : 'No'}</td>
                  <td className="muted">{item.pensionable_flag ? 'Yes' : 'No'}</td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted center">
                    No pay components yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add pay component' : 'Edit pay component'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label>
              Code *
              <input value={form.component_code} onChange={set('component_code')} required />
              {errors.component_code && <small className="error">{errors.component_code[0]}</small>}
            </label>
            <label>
              Name *
              <input value={form.component_name} onChange={set('component_name')} required />
              {errors.component_name && <small className="error">{errors.component_name[0]}</small>}
            </label>
            <label className="span-2">
              Type *
              <select value={form.component_type} onChange={set('component_type')} required>
                <option value="addition">Addition</option>
                <option value="deduction">Deduction</option>
              </select>
              {errors.component_type && <small className="error">{errors.component_type[0]}</small>}
            </label>
            <label className="checkbox-inline">
              <input type="checkbox" checked={form.recurring_flag} onChange={toggle('recurring_flag')} />
              Recurring
            </label>
            <label className="checkbox-inline">
              <input type="checkbox" checked={form.taxable_flag} onChange={toggle('taxable_flag')} />
              Taxable
            </label>
            <label className="checkbox-inline">
              <input type="checkbox" checked={form.pensionable_flag} onChange={toggle('pensionable_flag')} />
              Pensionable
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
